import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import { api } from "../lib/api";
import type { User } from "../types";

interface LoginResult {
  user?: User;
  mustChangePassword?: boolean;
  mfaRequired?: boolean;
  challengeId?: string;
  mfaSetupRequired?: boolean;
  status?: "AUTHENTICATED" | "MFA_REQUIRED";
}

interface AuthContextValue {
  user: User | null;
  loading: boolean;
  login: (email: string, password: string) => Promise<LoginResult>;
  logout: () => Promise<void>;
  refreshUser: () => Promise<User | null>;
}

const AuthContext = createContext<AuthContextValue | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);

  const refreshUser = useCallback(async () => {
    try {
      const responseUser = await api<User>("/auth/me");
      const nextUser = { ...responseUser, name: responseUser.displayName };
      setUser(nextUser);
      return nextUser;
    } catch {
      setUser(null);
      return null;
    }
  }, []);

  useEffect(() => {
    refreshUser().finally(() => setLoading(false));
  }, [refreshUser]);

  const login = useCallback(async (email: string, password: string) => {
    const result = await api<LoginResult>("/auth/login", {
      method: "POST",
      body: { identifier: email.trim(), password },
      skipRefresh: true,
    });
    if (result.mfaRequired) return result;
    if (!result.user) throw new Error("登录响应缺少用户信息");
    const nextUser = {
      ...result.user,
      name: result.user.displayName,
      mustChangePassword:
        result.mustChangePassword ?? result.user.mustChangePassword,
      mfaSetupRequired: result.mfaSetupRequired,
    };
    setUser(nextUser);
    return { ...result, user: nextUser };
  }, []);

  const logout = useCallback(async () => {
    try {
      await api("/auth/logout", { method: "POST", skipRefresh: true });
    } finally {
      setUser(null);
    }
  }, []);

  const value = useMemo(
    () => ({ user, loading, login, logout, refreshUser }),
    [user, loading, login, logout, refreshUser],
  );
  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const value = useContext(AuthContext);
  if (!value) throw new Error("useAuth 必须在 AuthProvider 内使用");
  return value;
}
